import * as React from 'react';
import { StatesButton, StatesButtonStates } from '..';
import { delay } from '../utils';
import { uniTestkitFactoryCreator } from '@wix/wix-ui-test-utils/vanilla';
import {
  StatesButtonDriver,
  statesButtonDriverFactory,
} from './StatesButton.driver';
import { onStyleProcessorDone } from '../../test/visual/StyleProcessorUtil';
import { visualize, snap, story } from 'storybook-snapper';
import { VisualAsync, EditorShell } from '../../test/visual/VisualAsync';

interface StatesButtonVisualProps {
  done(): void;
  onClickState: StatesButtonStates;
}

interface StatesButtonVisualState {
  buttonState: StatesButtonStates;
}

const createDriver = uniTestkitFactoryCreator(statesButtonDriverFactory);
const dataHook = 'storybook-e2e-StatesButton';

class StatesButtonVisual extends React.Component<
  StatesButtonVisualProps,
  StatesButtonVisualState
> {
  private driver: StatesButtonDriver;
  state = {
    buttonState: StatesButtonStates.IDLE,
  };

  componentDidMount(): void {
    this.driver = createDriver({ wrapper: document.body, dataHook });
    if (window.cssVars) {
      this.act();
    } else {
      onStyleProcessorDone()
        .then(this.act)
        .catch(() => {});
    }
  }

  async componentDidUpdate(
    // @ts-expect-error
    prevProps: Readonly<StatesButtonVisualProps>,
    prevState: Readonly<StatesButtonVisualState>,
  ) {
    const { buttonState } = this.state;

    if (
      buttonState === StatesButtonStates.IDLE &&
      prevState.buttonState !== buttonState
    ) {
      await delay(500);
      this.props.done();
    }
  }

  private readonly act = async () => {
    await delay(500);
    await this.driver.click();
  };

  render() {
    return (
      <EditorShell>
        <StatesButton
          disabled={false}
          state={this.state.buttonState}
          idleContent="Idle state"
          inProgressContent="Loading..."
          failureContent="Try Again"
          data-hook={dataHook}
          onClick={() => {
            this.setState({ buttonState: this.props.onClickState });
          }}
          onNotificationEnd={() =>
            this.setState({ buttonState: StatesButtonStates.IDLE })
          }
        />
      </EditorShell>
    );
  }
}

visualize('StatesButton', () => {
  Object.keys(StatesButtonStates).forEach((state) => {
    const buttonState = StatesButtonStates[state];

    snap(`${state}`, () => (
      <StatesButton
        state={buttonState}
        disabled={false}
        idleContent="My States Button"
        successContent="Success"
        inProgressContent="Loading..."
        failureContent="Try Again"
      />
    ));
  });

  snap('State change to success and back', (done) => (
    <StatesButtonVisual done={done} onClickState={StatesButtonStates.SUCCESS} />
  ));

  snap('State change to failure and back', (done) => (
    <StatesButtonVisual done={done} onClickState={StatesButtonStates.FAILURE} />
  ));

  const focusTests = Object.keys(StatesButtonStates).map((state) => {
    const buttonState = StatesButtonStates[state];

    return {
      it: state,
      props: {
        state: buttonState,
        disabled: false,
        idleContent: 'My States Button',
        successContent: 'Success',
        inProgressContent: 'Loading...',
        failureContent: 'Try Again',
      },
    };
  });

  const clickFocusTests = [
    {
      describe: 'focus',
      its: focusTests.map(({ it, props }) => ({
        it,
        props: { ...props },
      })),
    },
    {
      describe: 'focus disabled',
      its: focusTests.map(({ it, props }) => ({
        it,
        props: { ...props, disabled: true },
      })),
    },
  ];

  clickFocusTests.forEach(({ describe, its }) =>
    story(describe, () => {
      its.forEach(({ it, props }) =>
        snap(it, (done) => (
          <VisualAsync
            driverFactory={statesButtonDriverFactory}
            onDone={done}
            action={(driver) => driver.click()}
          >
            <StatesButton {...props} />
          </VisualAsync>
        )),
      );
    }),
  );
});
